import { initGoogleMapsApi } from '@egds/react-core/utils';

/* istanbul ignore file */
class ThirdPartyAdapter {
    locale;
    category;
    sourceName;
    type;
    apiKey;
    version;
    constructor(locale) {
        this.locale = locale;
    }
    toEssResult(predictionDescription, placeId, index) {
        const displayName = predictionDescription;
        let primaryDisplayName = '';
        let secondaryDisplayName = '';
        const parts = (displayName || '').split(',');
        if (parts.length < 2) {
            primaryDisplayName = parts[0].trim();
        }
        else {
            primaryDisplayName = parts[0];
            secondaryDisplayName = parts
                .slice(1)
                .map((word) => word.trim())
                .join(', ')
                .trim();
        }
        return {
            category: this.category,
            coordinates: undefined,
            index,
            regionNames: {
                displayName,
                fullName: predictionDescription,
                lastSearchName: predictionDescription,
                primaryDisplayName,
                secondaryDisplayName,
            },
            sourceId: placeId,
            sourceName: this.sourceName,
            type: this.type,
        };
    }
}

/* istanbul ignore file */
const GOOGLEMAPS_CATEGORY = 'GOOGLEMAPS';
var PlacesServiceStatus;
(function (PlacesServiceStatus) {
    PlacesServiceStatus["INVALID_REQUEST"] = "INVALID_REQUEST";
    PlacesServiceStatus["OK"] = "OK";
    PlacesServiceStatus["OVER_QUERY_LIMIT"] = "OVER_QUERY_LIMIT";
    PlacesServiceStatus["REQUEST_DENIED"] = "REQUEST_DENIED";
    PlacesServiceStatus["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
    PlacesServiceStatus["ZERO_RESULTS"] = "ZERO_RESULTS";
})(PlacesServiceStatus || (PlacesServiceStatus = {}));
class GoogleAdapter extends ThirdPartyAdapter {
    google;
    googleAutoCompleteService;
    placesService;
    ready;
    static instances = {};
    constructor(locale, apiKey, version) {
        super(locale);
        this.category = GOOGLEMAPS_CATEGORY;
        this.sourceName = 'GPA';
        this.type = 'ADDRESS';
        this.version = version;
        const conf = { accessToken: apiKey, locale, version };
        const timeoutDuration = 10000;
        const timeoutPromise = new Promise((resolve, reject) => {
            setTimeout(() => {
                reject(new Error('Timeout: Map API initialization took too long'));
            }, timeoutDuration);
        });
        this.ready = Promise.race([initGoogleMapsApi(false, conf, false), timeoutPromise])
            .then(() => {
            this.google = window.google;
            this.initGooglePlacesService();
            return true;
        })
            .catch((err) => {
            console.error(err);
            return false;
        });
    }
    static getInstance(locale, apiKey, version) {
        const thisLocale = locale || 'en_US';
        const thisGoogleApiKey = apiKey || 'AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68';
        if (!(thisLocale in this.instances)) {
            this.instances[thisLocale] = new GoogleAdapter(thisLocale, thisGoogleApiKey, version);
        }
        return this.instances[thisLocale];
    }
    isReady() {
        return this.ready;
    }
    initGooglePlacesService() {
        if (this.google && this.google.maps && this.google.maps.places) {
            const { AutocompleteService, PlacesService } = this.google.maps.places;
            this.googleAutoCompleteService = new AutocompleteService();
            this.placesService = new PlacesService(document.createElement('div'));
        }
    }
    fetchResults(essResponse) {
        const request = {
            input: essResponse.q,
            type: essResponse.rc === 'ADDRESS_AUTOCOMPLETE' ? ['geocode'] : undefined,
        };
        const toEssResponse = (results) => ({
            q: essResponse.q,
            rc: essResponse.rc,
            rid: essResponse.rid,
            sr: results,
        });
        return new Promise((resolve) => {
            if (this.googleAutoCompleteService) {
                this.googleAutoCompleteService.getPlacePredictions(request, (predictions, status) => {
                    if (status === PlacesServiceStatus.OK) {
                        const response = toEssResponse(this.handleResponse(predictions));
                        resolve(response);
                    }
                    else {
                        resolve(essResponse);
                    }
                });
            }
            else {
                resolve(essResponse);
            }
        });
    }
    fetchLocation(placeId) {
        return this.isReady().then((isReady) => new Promise((resolve, reject) => {
            if (!isReady === false) {
                try {
                    const request = {
                        placeId,
                        fields: ['geometry'],
                    };
                    this.placesService.getDetails(request, (place, status) => {
                        if (status === PlacesServiceStatus.OK) {
                            const loc = place?.geometry?.location;
                            if (loc)
                                resolve({
                                    lat: loc.lat().toFixed(5),
                                    lng: loc.lng().toFixed(5),
                                });
                            else
                                resolve(undefined);
                        }
                        else if (status === PlacesServiceStatus.ZERO_RESULTS)
                            resolve(undefined);
                        else
                            reject(new Error(`Failed to fetch location for placeId ${placeId}: ${status}`));
                    });
                }
                catch (error) {
                    reject(new Error(`Failed to fetch location for placeId ${placeId}: ${error.message}`));
                }
            }
            else {
                reject(new Error(`Unable to fetch location for placeId ${placeId}: PlacesService is not initialized`));
            }
        }));
    }
    handleResponse(predictions) {
        const results = [];
        predictions.forEach((prediction, index) => {
            results.push(this.toEssResult(prediction.description, prediction.place_id, index));
        });
        return results;
    }
}

/* istanbul ignore file */
const thirdPartyAdapterRegister = {
    google: GoogleAdapter,
};
class ThirdPartyAdapterFactory {
    static getInstance(k, locale, apiKey, version) {
        return thirdPartyAdapterRegister[k].getInstance(locale, apiKey, version);
    }
    static filterThirdPartyAdapterFromEssConfig(configFeaturesList) {
        return configFeaturesList.filter((v) => v in thirdPartyAdapterRegister);
    }
}

var TypeaheadLocationType;
(function (TypeaheadLocationType) {
    TypeaheadLocationType["HOTEL"] = "HOTEL";
    TypeaheadLocationType["AIRPORT"] = "AIRPORT";
    TypeaheadLocationType["MULTICITY"] = "MULTICITY";
    TypeaheadLocationType["POI"] = "POI";
    TypeaheadLocationType["METROCODE"] = "METROCODE";
    TypeaheadLocationType["METROSTATION"] = "METROSTATION";
    TypeaheadLocationType["TRAINSTATION"] = "TRAINSTATION";
    TypeaheadLocationType["ADDRESS"] = "ADDRESS";
    TypeaheadLocationType["NEIGHBORHOOD"] = "NEIGHBORHOOD";
    TypeaheadLocationType["CURRENT_LOCATION"] = "CURRENT_LOCATION";
    TypeaheadLocationType["PREMISE"] = "PREMISE";
    TypeaheadLocationType["BUSSTATION"] = "BUSSTATION";
    TypeaheadLocationType["ACTIVITY"] = "ACTIVITY";
    TypeaheadLocationType["ATTRACTION"] = "ATTRACTION";
    TypeaheadLocationType["CITY"] = "CITY";
})(TypeaheadLocationType || (TypeaheadLocationType = {}));
var EssFeatures;
(function (EssFeatures) {
    EssFeatures["GOOGLE"] = "google";
    EssFeatures["CONSISTENT_DISPLAY"] = "consistent_display";
})(EssFeatures || (EssFeatures = {}));
class ESSAdapter {
    config;
    thirdPartyFallbackAdapter;
    l10n;
    viewSize = 0;
    ESSCategories;
    isGeoLocationAllowed = true;
    response;
    constructor(config) {
        this.config = config;
        this.config.getDevice = this.getDevice;
    }
    setTypeaheadCategories() {
        const { formatText } = this.l10n;
        this.ESSCategories = {
            ACTIVITY: formatText('typeahead.ess.attraction'),
            ADDRESS: formatText('typeahead.ess.address'),
            AIRPORT: formatText('typeahead.ess.airport'),
            ATTRACTION: formatText('typeahead.ess.attraction'),
            BUSSTATION: formatText('typeahead.ess.transit'),
            CITY: formatText('typeahead.ess.city'),
            HOTEL: formatText('typeahead.ess.hotel'),
            LISTING: formatText('typeahead.ess.property'),
            METROCODE: formatText('typeahead.ess.city'),
            METROSTATION: formatText('typeahead.ess.transit'),
            MULTICITY: formatText('typeahead.ess.city'),
            MULTIREGION: formatText('typeahead.ess.city'),
            NEIGHBORHOOD: formatText('typeahead.ess.city'),
            POI: formatText('typeahead.ess.attraction'),
            PREMISE: formatText('typeahead.ess.property'),
            TRAINSTATION: formatText('typeahead.ess.transit'),
            CURRENT_LOCATION: '',
        };
    }
    isHistoryDetailEnabled() {
        return this.config.historyDetail === true;
    }
    getConfig() {
        return this.config;
    }
    getTypeaheadCategories() {
        return this.ESSCategories;
    }
    addToESSCategories(newCategory) {
        this.ESSCategories = { ...this.ESSCategories, ...newCategory };
    }
    setL10n(l10n) {
        this.l10n = l10n;
    }
    createURLParams(params) {
        return Object.entries(params)
            .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
            .join('&');
    }
    fetchDeleteRecentSearch(regionId) {
        const domain = this.config.domain.endsWith('/') ? this.config.domain : `${this.config.domain}/`;
        const baseURL = `${domain}api/v1/deletehistory`;
        const config = {
            lob: this.config.lob,
            locale: this.config.locale,
            guid: this.config.guid,
            expuserid: this.config.expuserid,
            siteId: this.config.siteId,
            dest: this.config.dest,
        };
        const url = `${baseURL}?${this.createURLParams(config)}`;
        const headers = new Headers({
            accept: 'application/json',
            'Content-Type': 'application/json',
        });
        if (regionId !== '') {
            const body = {
                gaiaIds: [regionId],
            };
            return fetch(url, {
                body: JSON.stringify(body),
                headers,
                method: 'POST',
            })
                .then((response) => response.json())
                .catch((error) => {
                console.error(`Error while deleting recent searches: ${error}`);
            });
        }
        return Promise.resolve([]);
    }
    constructURL(query, makeAltDestCall) {
        let domain = this.config.domain ? this.config.domain : '';
        domain = domain.endsWith('/') ? domain : `${domain}/`;
        if (domain.endsWith('/')) {
            domain = domain.slice(0, -1);
        }
        const getBaseURL = () => {
            switch (this.config.domainId) {
                case 'AIRLINE':
                    return `${domain}/api/v2/suggest`;
                default:
                    return `${domain}/api/v4/typeahead`;
            }
        };
        const baseURL = getBaseURL();
        if (typeof this.config.browser === 'undefined' || this.config.browser === null) {
            this.config.browser = this.getBrowser(window.navigator.userAgent);
        }
        if (typeof this.config.device === 'undefined' || this.config.device === null) {
            this.config.device = this.getDevice(window.navigator.userAgent);
        }
        if (this.config.domainId === 'AIRLINE') {
            const configV2 = {
                domainId: this.config.domainId,
                lob: this.config.lob,
                maxResults: this.config.maxResults,
                siteid: this.config.siteId,
                locale: this.config.locale,
            };
            return `${baseURL}?query=${query}&${this.createURLParams(configV2)}`;
        }
        const config = {
            browser: this.config.browser,
            client: this.config.client,
            dest: this.config.dest,
            device: this.config.device,
            expuserid: this.config.expuserid ? this.config.expuserid : -1,
            features: this.config.features,
            format: 'json',
            guid: this.config.guid,
            listing: !!this.config.listing,
            lob: this.config.lob,
            locale: this.config.locale,
            maxresults: this.config.maxResults,
            personalize: this.config.personalize ? this.config.personalize : false,
            ...(this.config.regionId ? { rid: this.config.regionId } : {}),
            regiontype: this.config.regionType,
            siteid: this.config.siteId,
            ignorePrefillSearchTermForAlternateDestinationsRequest: this.config.ignorePrefillSearchTermForAlternateDestinationsRequest,
            ...(this.config.packageType ? { pt: this.config.packageType } : {}),
            ...(this.config.latLong ? { latlong: this.config.latLong } : {}),
            ...(this.config.lastSearchRegionId ? { lsrid: this.config.lastSearchRegionId } : {}),
            ...(this.config.lastSearchTimestamp ? { lstime: this.config.lastSearchTimestamp } : {}),
            ...(this.config.lastSearchLatLong ? { lslatlong: this.config.lastSearchLatLong } : {}),
            ...(this.config.lastSearchElapseTime ? { lsetime: this.config.lastSearchElapseTime } : {}),
            ...(this.config.googleApiKey ? { googleApiKey: this.config.googleApiKey } : {}),
            ...(this.config.googleApiVersion ? { googleApiVersion: this.config.googleApiVersion } : {}),
            ...(this.config.alternate && !!makeAltDestCall ? { alternate: true } : {}),
            ...(this.config.debounceVariant ? { ab: this.config.debounceVariant } : {}),
            ...(this.config.trending ? { trending: this.config.trending } : {}),
            ...(this.config.alternateSecondVariant ? { alternateSecondVariant: this.config.alternateSecondVariant } : {}),
            ...(this.config.drivable ? { drivable: this.config.drivable } : {}),
            ...(this.config.fetchLocation ? { fetchLocation: this.config.fetchLocation } : {}),
            ...(this.config.popularFilter ? { popularFilter: this.config.popularFilter } : {}),
        };
        const url = query === ''
            ? `${baseURL}/?${this.createURLParams(config)}`
            : `${baseURL}/${query}?${this.createURLParams(config)}`;
        return url;
    }
    fetchResponse(term, makeAltDestCall) {
        const personalize = this.config && this.config.personalize;
        if ((term === '' && personalize) || term !== '') {
            this.config.logRequest?.(term);
            return fetch(this.constructURL(term, makeAltDestCall), {
                method: this.config.domainId === 'AIRLINE' ? 'POST' : 'GET',
                ...(this.config.xPageId && { headers: { 'x-page-id': this.config.xPageId } }),
            }).then((response) => response.json());
        }
        return Promise.resolve([]);
    }
    fetchResults(term, makeAltDestCall) {
        const searchTerm = makeAltDestCall ? '' : term;
        return this.fetchResponse(searchTerm, makeAltDestCall)
            .then((json) => this.handleEssResponse(json))
            .then((json) => this.logResponse(json))
            .then((json) => this.adaptResponseToTypeaheadResults(json, searchTerm, this.config.historyDetail, this.config.historyDetailFormatVariant))
            .then((json) => this.categorizeOnFocusResponse(json, makeAltDestCall));
    }
    fetchRecentSearchResults() {
        return this.fetchResults('');
    }
    fetchAlternativeResults(term) {
        return this.fetchResults(term, this.config.alternate || this.config.ignorePrefillSearchTermForAlternateDestinationsRequest);
    }
    fetchResultsCategorized(term) {
        return this.fetchResponse(term)
            .then((json) => this.handleEssResponse(json))
            .then((json) => this.logResponse(json))
            .then((json) => this.adaptResponseToTypeaheadResults(json, term, this.config.historyDetail, this.config.historyDetailFormatVariant))
            .then((json) => this.categorizeResponse(json));
    }
    fetchRecentSearchResultsCategorized() {
        return this.fetchResultsCategorized('');
    }
    categorizeResponse(typeaheadResults) {
        const resultsByCategory = new Map();
        const finalResults = [];
        let trueIndex = 0;
        if (typeaheadResults && typeaheadResults.length > 0) {
            // Creating categories and inserting to a map
            typeaheadResults.forEach((element) => {
                const categoryName = element?.data?.type ? this.ESSCategories[element.data.type] : '';
                if (!resultsByCategory[categoryName]) {
                    resultsByCategory[categoryName] = [];
                }
                resultsByCategory[categoryName].push(element);
            });
            for (const [heading, results] of Object.entries(resultsByCategory)) {
                // Pushing to an array of CategorizedResults
                // eslint-disable-next-line no-loop-func
                results.forEach((typeaheadResult) => {
                    // eslint-disable-next-line no-param-reassign
                    typeaheadResult.data.index = trueIndex;
                    // eslint-disable-next-line no-plusplus
                    trueIndex++;
                });
                const resultByCategory = {
                    heading,
                    typeaheadResults: results,
                };
                finalResults.push(resultByCategory);
            }
        }
        return finalResults;
    }
    onFocusCategories() {
        const { formatText } = this.l10n;
        return {
            CURRENTLOCATION: '',
            POPULAR: formatText('typeahead.ess.popular') || 'Explore popular destinations',
            DRIVABLE: formatText('typeahead.ess.nearby') || 'Nearby locations',
            ...(this.config.alternateSecondVariant
                ? {
                    USERHISTORY: formatText('typeahead.ess.history') || 'Recent searches',
                    ALTERNATE: formatText('typeahead.ess.alternate') || 'People also searched',
                }
                : {
                    ALTERNATE: formatText('typeahead.ess.alternate') || 'People also searched',
                    USERHISTORY: formatText('typeahead.ess.history') || 'Recent searches',
                }),
        };
    }
    categorizeOnFocusResponse(typeaheadResults, makeAltDestCall) {
        if (typeaheadResults && typeaheadResults.length > 0) {
            const element0 = typeaheadResults[0];
            if (element0.data.category === 'ALTERNATE' ||
                element0.data.category === 'CURRENTLOCATION' ||
                element0.data.category === 'POPULAR' ||
                (element0.data.category === 'DRIVABLE' && this.config.drivable) ||
                (element0.data.category === 'USERHISTORY' && this.config.alternate && makeAltDestCall)) {
                const resultsByCategory = new Map();
                const finalResults = [];
                const focusCategories = this.onFocusCategories();
                let trueIndex = 0;
                typeaheadResults.forEach((element) => {
                    if (element.data.category) {
                        const categoryName = element.data.category;
                        if (!resultsByCategory[categoryName]) {
                            resultsByCategory[categoryName] = [];
                        }
                        resultsByCategory[categoryName].push(element);
                    }
                });
                for (const [category, heading] of Object.entries(focusCategories)) {
                    const categorizedResult = resultsByCategory[category];
                    if (categorizedResult && categorizedResult.length > 0) {
                        // eslint-disable-next-line no-loop-func
                        categorizedResult.forEach((typeaheadResult) => {
                            // eslint-disable-next-line no-param-reassign
                            typeaheadResult.data.index = trueIndex;
                            // eslint-disable-next-line no-param-reassign
                            typeaheadResult.data.displayType = 'CATEGORIZED';
                            trueIndex += 1;
                        });
                        const resultByCategory = {
                            heading,
                            typeaheadResults: categorizedResult,
                        };
                        finalResults.push(resultByCategory);
                    }
                }
                if (makeAltDestCall || this.config.showCurrentLocation) {
                    // Log the typeahead response in the logger with the updated order
                    this.response.sr = finalResults.flatMap((categorizedResult) => categorizedResult.typeaheadResults.map((typeaheadResult) => typeaheadResult.data));
                    this.config.logResponse?.(this.response);
                }
                return finalResults;
            }
        }
        return typeaheadResults;
    }
    onItemSelection(data) {
        if (data?.type === TypeaheadLocationType.CURRENT_LOCATION) {
            return this.handleCurrentLocationSelection(data);
        }
        if (this.config.fetchLocation &&
            data?.type === TypeaheadLocationType.ADDRESS &&
            data.category === GOOGLEMAPS_CATEGORY &&
            data.sourceId &&
            typeof this.thirdPartyFallbackAdapter.fetchLocation === 'function') {
            return this.fillLocation(this.thirdPartyFallbackAdapter.fetchLocation.bind(this.thirdPartyFallbackAdapter), data.sourceId, data);
        }
        return Promise.resolve(data);
    }
    handleCurrentLocationSelection(data) {
        if (this.isGeoLocationAllowed) {
            return this.readCurrentLocation()
                .then(({ coords: { latitude, longitude } }) => ({
                ...data,
                location: { lat: latitude.toFixed(5), long: longitude.toFixed(5) },
            }))
                .catch((error) => {
                this.isGeoLocationAllowed = false;
                throw error;
            });
        }
        const message = this.l10n.formatText('typeahead.currentLocation.blocked.message');
        // eslint-disable-next-line no-alert
        window.alert(message);
        return Promise.reject(message);
    }
    fillLocation(fetchLocation, placeId, data) {
        return fetchLocation(placeId).then((location) => {
            if (location) {
                const { lat, lng } = location;
                return {
                    location: {
                        lat,
                        long: lng,
                    },
                    ...data,
                };
            }
            return data;
        });
    }
    /*
     * Note: Position is a global TypeScript interface representing the response to the navigator.geolocation call.
     * https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.position.html
     */
    readCurrentLocation() {
        return new Promise((resolve, reject) => {
            window.navigator.geolocation.getCurrentPosition(resolve, reject);
        });
    }
    handleEssResponse(json) {
        // eslint-disable-next-line no-param-reassign
        json.sr = json.sr || json.suggestions || [];
        if (!this.thirdPartyFallbackAdapter) {
            this.setThirdPartyAdapter();
        }
        if (this.thirdPartyFallbackAdapter && (json.rc === 'GOOGLE_AUTOCOMPLETE' || json.rc === 'ADDRESS_AUTOCOMPLETE')) {
            return this.thirdPartyFallbackAdapter.fetchResults(json);
        }
        return Promise.resolve(json);
    }
    setThirdPartyAdapter() {
        const fallbackAdapterRequested = ThirdPartyAdapterFactory.filterThirdPartyAdapterFromEssConfig(this.config.features.split('|'));
        let fallbackAdapter;
        if (fallbackAdapterRequested.length > 0) {
            const fallbackAdapterKey = fallbackAdapterRequested[0];
            fallbackAdapter = ThirdPartyAdapterFactory.getInstance(fallbackAdapterKey, this.config.locale, fallbackAdapterKey === 'google' ? this.config.googleApiKey : undefined, fallbackAdapterKey === 'google' ? this.config.googleApiVersion : undefined);
            this.thirdPartyFallbackAdapter = fallbackAdapter;
        }
    }
    logResponse(results) {
        this.config.logResponse?.(results);
        this.response = results;
        return Promise.resolve(results);
    }
    adaptResponseToTypeaheadResults(results, term, historyDetail, historyDetailVariant) {
        const getOneRoomLodgingString = (formatText, rooms, travelers, excludeRoomsLabelForRecentHistory) => excludeRoomsLabelForRecentHistory
            ? formatText('typeahead.recentHistory.travelers', travelers)
            : historyDetailVariant
                ? formatText('typeahead.recentHistory.travelersRooms', travelers, rooms)
                : formatText('typeahead.recentHistory.roomsTravelers', rooms, travelers);
        const getListingTitle = (result) => result?.type === 'LISTING' && result.listingProps ? result.listingProps.headline : undefined;
        const getListingSubText = (result) => result?.type === 'LISTING' && result.listingProps
            ? { subtext: `${result.listingProps.listingId} - ${result.listingProps.baseLocation}` }
            : undefined;
        const getSubtext = (category, data) => {
            let subtextString;
            let subtextA11yLabel;
            if (category === 'USERHISTORY' &&
                historyDetail &&
                typeof this.l10n !== 'undefined' &&
                data &&
                data.searchDetail &&
                data.searchDetail.lob &&
                !term) {
                let summaryString = '';
                const detail = data.searchDetail;
                const { formatDateString, formatText } = this.l10n;
                const rooms = detail.roomCount;
                const lineOfBusiness = detail.lob;
                const travelers = data.searchDetail.totalTravelerCount ? Number(data.searchDetail.totalTravelerCount) : null;
                if (data.searchDetail.startDate && data.searchDetail.endDate) {
                    // `TimezoneOffset` is used to prevent the days being off by one day because of local time conversion
                    const checkinDate = new Date(detail.startDate);
                    const checkinTimezoneOffset = checkinDate.getTimezoneOffset() * 60000;
                    const checkinString = formatDateString(new Date(checkinDate.getTime() + checkinTimezoneOffset), {
                        skeleton: 'MMMd',
                    });
                    const checkoutDate = new Date(detail.endDate);
                    const checkoutTimezoneOffset = checkoutDate.getTimezoneOffset() * 60000;
                    const checkoutString = formatDateString(new Date(checkoutDate.getTime() + checkoutTimezoneOffset), {
                        skeleton: 'MMMd',
                    });
                    // Dates are in milliseconds, so divide by ms/day to get number of days/nights
                    const nights = (Number(detail.endDate) - Number(detail.startDate)) / 86400000;
                    const nightsString = formatText('datePicker.nightSummary', nights);
                    subtextString = `${checkinString} - ${checkoutString}`;
                    subtextA11yLabel = formatText('typeahead.recentHistory.aceessibility.dates', checkinString, checkoutString);
                    if (rooms === 1 && (lineOfBusiness === 'Lodging' || lineOfBusiness === 'Package')) {
                        summaryString = getOneRoomLodgingString(formatText, rooms, travelers, this.config.excludeRoomsLabelForRecentHistory);
                        if (historyDetailVariant) {
                            subtextString = `${checkinString} - ${checkoutString} • ${summaryString}`;
                            subtextA11yLabel = subtextA11yLabel.concat(`, ${summaryString}`);
                        }
                        else {
                            subtextString = `${checkinString} - ${checkoutString} | ${nightsString} | ${summaryString}`;
                            subtextA11yLabel = subtextA11yLabel.concat(`, ${nightsString}, ${summaryString}`);
                        }
                    }
                    else if (lineOfBusiness === 'Lodging' || lineOfBusiness === 'Package') {
                        if (historyDetailVariant) {
                            subtextString = `${checkinString} - ${checkoutString}`;
                        }
                        else {
                            subtextString = `${checkinString} - ${checkoutString} | ${nightsString}`;
                            subtextA11yLabel = subtextA11yLabel.concat(`, ${nightsString}`);
                        }
                    }
                }
                else if (!data.searchDetail.startDate && !data.searchDetail.endDate) {
                    if (rooms === 1 && (lineOfBusiness === 'Lodging' || lineOfBusiness === 'Package')) {
                        subtextString = getOneRoomLodgingString(formatText, rooms, travelers, this.config.excludeRoomsLabelForRecentHistory);
                    }
                }
            }
            return { subtext: subtextString, a11yLabel: subtextA11yLabel };
        };
        const parseDisplayText = (result) => {
            const { subtext, a11yLabel } = getSubtext(result.category, result) || getListingSubText(result);
            if (this.config.features.indexOf(EssFeatures.CONSISTENT_DISPLAY) < 0) {
                const oneLineDisplayText = result.regionNames?.displayName || result.names?.displayName || getListingTitle(result);
                return { primaryText: oneLineDisplayText, subtext, a11yLabel };
            }
            const firstLine = result.regionNames?.primaryDisplayName || result.names?.displayName || getListingTitle(result);
            if (subtext) {
                return {
                    primaryText: `${firstLine}`,
                    subtext,
                    a11yLabel,
                };
            }
            const secondLine = result.regionNames?.secondaryDisplayName || getListingSubText(result) || '';
            return {
                primaryText: `${firstLine}`,
                subtext: secondLine,
            };
        };
        // eslint-disable-next-line max-params
        const getIcon = (type, subType, isChild, isNearby, category) => {
            if (type === 'AIRLINE') {
                return '';
            }
            if (isChild && !isNearby) {
                return 'subdirectory_arrow_right';
            }
            if (category === 'USERHISTORY') {
                return 'history';
            }
            if (this.config.lob === 'FLIGHTS') {
                return 'lob_flights';
            }
            switch (type) {
                case 'HOTEL':
                    return 'lob_hotels';
                case 'AIRPORT':
                    return 'lob_flights';
                case 'ADDRESS':
                    if (subType === 'RENTALLOCATION') {
                        return 'lob_cars';
                    }
                    return 'place';
                case 'METROSTATION':
                case 'TRAINSTATION':
                    return 'train';
                default:
                    return 'place';
            }
        };
        const generateNewResults = (newResults, result, index) => {
            const isNearby = (result.hierarchyInfo &&
                result.hierarchyInfo.relation &&
                result.hierarchyInfo.relation.indexOf('nearby') > -1);
            const isChild = (result.hierarchyInfo &&
                result.hierarchyInfo.relation &&
                result.hierarchyInfo.relation.indexOf('child') > -1) || isNearby;
            const { primaryText, subtext, a11yLabel } = parseDisplayText(result);
            const newResult = {
                data: {
                    ...(result.category ? { category: result.category } : {}),
                    ...(!term && result.searchDetail ? { searchDetail: result.searchDetail } : {}),
                    ...(result.userHistoryType ? { userHistoryType: result.userHistoryType } : {}),
                    ...(result.ids?.id ? { id: result.ids.id } : {}),
                    ...(result.coordinates
                        ? {
                            location: {
                                lat: result.coordinates.lat,
                                long: result.coordinates.long,
                            },
                        }
                        : {}),
                    ...(result.regionId ? { regionId: result.regionId } : result.gaiaId ? { regionId: result.gaiaId } : {}),
                    ...(result.cityId ? { cityId: result.cityId } : {}),
                    ...(result.regionNames ? { regionNames: result.regionNames } : {}),
                    ...(result.type ? { type: result.type } : {}),
                    ...(result.subtype ? { subtype: result.subtype } : {}),
                    ...(result.hotelId ? { selected: result.hotelId } : {}),
                    ...(result.hierarchyInfo ? { hierarchyInfo: result.hierarchyInfo } : {}),
                    ...(result.type === 'LISTING' && result.listingProps ? { listing: result.listingProps } : {}),
                    ...(result.sourceId ? { sourceId: result.sourceId } : {}),
                    ...(result.essId
                        ? {
                            essId: {
                                sourceId: result.essId.sourceId,
                                sourceName: result.essId.sourceName,
                            },
                        }
                        : {}),
                    ...{ dest: this.config.dest },
                    ...{ index: index + 1 },
                },
                icon: getIcon(result.type || result.domainId, result.subtype, isChild, isNearby, result.category),
                ...(isChild ? { isChild: true } : {}),
                imageUrl: result.imageUrl,
                name: primaryText,
                subtext,
                a11yLabel,
                title: result.regionNames?.lastSearchName ||
                    result.regionNames?.shortName ||
                    result.regionNames?.fullName ||
                    result.listingProps?.listingId ||
                    result.ids?.id,
                value: result.regionNames?.lastSearchName ||
                    result.regionNames?.shortName ||
                    result.regionNames?.fullName ||
                    result.listingProps?.listingId ||
                    result.names?.displayName,
            };
            newResults.push(newResult);
            if (result?.filterRefinements && result?.filterRefinements?.length > 0) {
                result?.filterRefinements
                    .find((item) => item?.category?.legacyId === 'popularFilter' || item?.category?.externalId === 'popular_group')
                    ?.arrayOfValues?.forEach((popularFilter) => {
                    newResults.push({
                        ...newResult,
                        data: {
                            ...newResult.data,
                            popularFilter: [popularFilter],
                        },
                        subtext: `<strong>${popularFilter?.displayName}</strong>`,
                        icon: 'subdirectory_arrow_right',
                    });
                });
            }
            return newResults;
        };
        const baseResults = term === '' && this.shouldShowCurrentLocation() ? [this.getCurrentLocationItem()] : [];
        const adapt = (json) => json.sr?.reduce?.(generateNewResults, baseResults) || baseResults;
        return adapt(results);
    }
    /**
     * The Current Location feature is only shown on browsers which support it,
     * even when the showCurrentLocation config is set to true.
     */
    shouldShowCurrentLocation() {
        return Boolean(this.config.showCurrentLocation && 'geolocation' in navigator);
    }
    /**
     * The "Current location" result item is not returned by the back-end EGTA/ESS API, it is instead created
     * on the front-end and added to the beginning of the results when needed.
     */
    getCurrentLocationItem() {
        const label = this.isGeoLocationAllowed
            ? this.l10n.formatText('typeahead.currentLocation.available.item')
            : this.l10n.formatText('typeahead.currentLocation.blocked.item');
        return {
            data: {
                category: 'CURRENTLOCATION',
                hierarchyInfo: {
                    relation: ['current'],
                },
                index: -1,
                enabled: this.isGeoLocationAllowed,
                regionNames: {
                    fullName: 'CurrentLocation',
                },
                type: TypeaheadLocationType.CURRENT_LOCATION,
            },
            icon: 'my_location',
            name: label,
            title: label,
            value: label,
        };
    }
    getBrowser(ua) {
        let tem;
        let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            return 'IE';
        }
        if (M[1] === 'Chrome') {
            tem = ua.match(/\b(OPR|Edge?)\/(\d+)/);
            if (tem !== null) {
                return tem.slice(1, -1).join(' ').replace('OPR', 'Opera').replace('Edg ', 'Edge ');
            }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
        tem = ua.match(/version\/(\d+)/i);
        if (tem !== null) {
            M.splice(1, 1, tem[1]);
        }
        return M[0];
    }
    getDevice(ua) {
        if (/iPad|Transformer|ME173X|KOOF|Nook|IMM76D|Nexus 10|Nexus 7|Tablet/i.test(ua) ||
            /HP Slate|PG09410|PG41200|Mediapad|Kindle|Tab|Xoom|TegraNote|SCH-I800/i.test(ua)) {
            return 'Tablet';
        }
        if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
            return 'Mobile';
        }
        return 'Desktop';
    }
}

const getSizeOfTypeaheadResults = (typeaheadResults) => {
    /* eslint-disable-next-line default-case */
    switch (typeaheadResults.type) {
        case 'categorized':
            return typeaheadResults.items.reduce((sum, currentItem) => sum + currentItem.typeaheadResults.length, 0);
        case 'uncategorized':
            return typeaheadResults.items.length;
    }
};
const filterTypeaheadResults = (typeaheadResults, values) => {
    /* eslint-disable-next-line default-case */
    switch (typeaheadResults.type) {
        case 'categorized':
            return {
                ...typeaheadResults,
                items: typeaheadResults.items.map((category) => ({
                    ...category,
                    typeaheadResults: category.typeaheadResults.filter((item) => !values.includes(item.value)),
                })),
            };
        case 'uncategorized':
            return {
                ...typeaheadResults,
                items: typeaheadResults.items.filter((item) => !values.includes(item.value)),
            };
    }
};
const getSelectedResult = (typeaheadResults, index) => {
    /* eslint-disable-next-line default-case */
    switch (typeaheadResults.type) {
        case 'categorized': {
            return typeaheadResults.items.reduce((items, currentItem) => {
                items.push(...currentItem.typeaheadResults);
                return items;
            }, [])[index];
        }
        case 'uncategorized':
            return typeaheadResults.items[index];
    }
};

/* istanbul ignore file */
const appendQueryParam = (url, key, value) => {
    if (value === '' || value === null) {
        return url;
    }
    const punctuation = /\?/.test(url) ? '&' : '?';
    let query = encodeURIComponent(key);
    if (value !== undefined && value !== '') {
        query = query.concat('=', encodeURIComponent(value));
    }
    return url + punctuation + query;
};
const appendQueryObject = (url, queryObject) => {
    if (queryObject === null || queryObject === undefined) {
        return url;
    }
    let result = url;
    Object.keys(queryObject).forEach((key) => {
        const value = queryObject[key];
        if (value === undefined) {
            return;
        }
        result = appendQueryParam(result, key, value);
    });
    return result;
};

export { ESSAdapter as E, GOOGLEMAPS_CATEGORY as G, PlacesServiceStatus as P, TypeaheadLocationType as T, ThirdPartyAdapterFactory as a, ThirdPartyAdapter as b, GoogleAdapter as c, getSelectedResult as d, appendQueryParam as e, filterTypeaheadResults as f, getSizeOfTypeaheadResults as g, appendQueryObject as h };
//# sourceMappingURL=index-chunk.esm.js.map
